import { Clock } from 'lucide-react';
import { useMemo } from 'react';
import type { TriageDensity } from '../../hooks/useTriageDensity';
import { groupThreadsByInbox } from '../../lib/clinic-logic';
import type { EmailThreadMetadata, LabelCategoryMapping, TriageCategory } from '../../types/email';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../ui/accordion';
import { Badge } from '../../ui/badge';
import { TriageEmptyState } from './TriageEmptyState';
import { TriageThreadItem, triageListClassName } from './TriageThreadItem';

interface TriageInboxViewProps {
  threads: EmailThreadMetadata[];
  isLoading: boolean;
  density?: TriageDensity;
  labelMap?: Record<string, string>;
  mappings?: LabelCategoryMapping[];
  categories?: TriageCategory[];
  workspaceMembers?: Array<{ id: number; email: string; fullName: string | null }>;
  onArchive: (threadId: string) => void;
  onDelegate: (threadId: string) => void;
  onCreateTask: (threadId: string) => void;
  onTaskClick: (taskId: number) => void;
  onCardClick: (threadId: string) => void;
  /** Re-run analysis for one thread; shown only when the failure is retryable. */
  onRetryAnalysis?: (threadId: string) => void;
  isReadOnly?: boolean;
}

export function TriageInboxView({
  threads,
  isLoading,
  density,
  labelMap,
  mappings,
  categories,
  workspaceMembers,
  onArchive,
  onDelegate,
  onCreateTask,
  onTaskClick,
  onCardClick,
  onRetryAnalysis,
  isReadOnly,
}: TriageInboxViewProps) {
  const groups = useMemo(() => groupThreadsByInbox(threads), [threads]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Clock className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (groups.length === 0) {
    return <TriageEmptyState />;
  }

  return (
    <Accordion type="multiple" defaultValue={groups.map((g) => g.accountEmail)}>
      {groups.map((group) => (
        <AccordionItem key={group.accountEmail} value={group.accountEmail}>
          <AccordionTrigger>
            <div className="flex items-center gap-2">
              <span className="font-semibold">{group.label}</span>
              <Badge variant="outline" className="text-[10px]">
                {group.threads.length}
              </Badge>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className={triageListClassName(density)}>
              {group.threads.map((thread) => (
                <TriageThreadItem
                  key={thread.threadId}
                  thread={thread}
                  density={density}
                  labelMap={labelMap}
                  mappings={mappings}
                  categories={categories}
                  workspaceMembers={workspaceMembers}
                  onArchive={onArchive}
                  onDelegate={onDelegate}
                  onCreateTask={onCreateTask}
                  onTaskClick={onTaskClick}
                  onCardClick={onCardClick}
                  onRetryAnalysis={onRetryAnalysis}
                  isReadOnly={isReadOnly}
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
